/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	HostListener,
	Injector,
	OnInit,
	Signal,
	WritableSignal,
	afterNextRender,
	computed,
	signal,
	viewChild
} from '@angular/core';
import {
	takeUntilDestroyed
} from '@angular/core/rxjs-interop';
import {
	Router
} from '@angular/router';
import {
	IEntityTypeDto
} from '@api/interfaces/entities/entity-type.dto.interface';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	IInsuranceSearchEntry
} from '@insurance/interfaces/insurance-search/insurance-search-entry.interface';
import {
	IInsuranceSearchFieldDisplay
} from '@insurance/interfaces/insurance-search/insurance-search-field-display.interface';
import {
	IInsuranceSearchGroupDisplayConfig
} from '@insurance/interfaces/insurance-search/insurance-search-group-display-config.interface';
import {
	IInsuranceSearchGroupDisplay
} from '@insurance/interfaces/insurance-search/insurance-search-group-display.interface';
import {
	IInsuranceSearchRequest
} from '@insurance/interfaces/insurance-search/insurance-search-request.interface';
import {
	IInsuranceSearchResultDisplay
} from '@insurance/interfaces/insurance-search/insurance-search-result-display.interface';
import {
	IInsuranceSearchResultGroup
} from '@insurance/interfaces/insurance-search/insurance-search-result-group.interface';
import {
	IInsuranceSearchResultDto
} from '@insurance/interfaces/insurance-search/insurance-search-result.dto.interface';
import {
	AppEventConstants
} from '@shared/constants/app-event.constants';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	SecurityRightType
} from '@shared/constants/enums/security-right-type.enum';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	SecurityHelper
} from '@shared/helpers/security.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	ISecurityEntityTypeDefinition
} from '@shared/interfaces/security/security-entity-type-definition.interface';
import {
	SessionService
} from '@shared/services/session.service';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';
import {
	DateTime
} from 'luxon';
import {
	Observable,
	Subject,
	Subscription,
	debounceTime,
	from,
	switchMap
} from 'rxjs';

/* eslint-enable max-len */

/**
 * Tracks the paging cursor, continuation eligibility, and async loading
 * state for a single result group.
 */
interface IGroupPagingState
{
	lastId?: number;
	hasMore: boolean;
	loading: boolean;
	loadError: boolean;
}

@Component({
	selector: 'app-insurance-search',
	templateUrl: './insurance-search.component.html',
	styleUrls: [
		'./insurance-search.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})

/**
 * A component representing the insurance search drawer. Accepts
 * a search term, calls the InsuranceSearch workflow action with
 * a batched array of entity type group requests, and displays
 * the grouped results with expandable cards.
 *
 * @export
 * @class InsuranceSearchComponent
 * @implements {IDynamicComponent<Component, any>}
 */
export class InsuranceSearchComponent
implements IDynamicComponent<Component, any>, OnInit
{
	/**
	 * Creates an instance of InsuranceSearchComponent.
	 *
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance API service used to execute the search action.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type API service used to execute the search action.
	 * @param {SessionService} sessionService
	 * The session service used to manage user sessions.
	 * @param {Router} router
	 * The router used to navigate to a selected search result.
	 * @param {DestroyRef} destroyRef
	 * The destroy reference used for subscription cleanup.
	 * @param {ElementRef} elementRef
	 * The host element reference used for DOM measurements.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used to respond to layout changes.
	 * @param {Injector} injector
	 * The injector used to schedule post-render callbacks outside the
	 * injection context.
	 * @memberof InsuranceSearchComponent
	 */
	public constructor(
		public entityInstanceApiService: EntityInstanceApiService,
		public entityTypeApiService: EntityTypeApiService,
		public sessionService: SessionService,
		public router: Router,
		private readonly destroyRef: DestroyRef,
		private readonly elementRef: ElementRef<HTMLElement>,
		public readonly siteLayoutService: SiteLayoutService,
		private readonly injector: Injector)
	{
		this.heightRecalculationSubject
			.pipe(
				debounceTime(AppConstants.time.tenMilliseconds),
				takeUntilDestroyed(this.destroyRef))
			.subscribe(
				() =>
				{
					this.recalculateGroupHeights();
				});
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization.
	 *
	 * @type {IDynamicComponentContext<Component, any>}
	 * @memberof InsuranceSearchComponent
	 */
	public context: IDynamicComponentContext<Component, any>;

	/**
	 * The current search term entered by the user.
	 *
	 * @type {WritableSignal<string>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly searchTerm: WritableSignal<string> =
		signal<string>(AppConstants.empty);

	/**
	 * Whether a search request is currently in progress.
	 *
	 * @type {WritableSignal<boolean>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly loading: WritableSignal<boolean> =
		signal<boolean>(false);

	/**
	 * Whether a search has been executed at least once.
	 *
	 * @type {WritableSignal<boolean>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly hasSearched: WritableSignal<boolean> =
		signal<boolean>(false);

	/**
	 * Whether the most recent initial search request failed with an error.
	 *
	 * @type {WritableSignal<boolean>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly initialSearchError: WritableSignal<boolean> =
		signal<boolean>(false);

	/**
	 * The validation message shown below the search input when
	 * the search term does not meet requirements.
	 *
	 * @type {WritableSignal<string>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly validationMessage: WritableSignal<string> =
		signal<string>(AppConstants.empty);

	/**
	 * Live validation message derived from the current search term.
	 * Non-empty only when the user has typed something that fails
	 * validation, so the message and disabled state appear
	 * immediately without requiring a search attempt.
	 *
	 * @type {Signal<string>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly inputValidationMessage: Signal<string> =
		computed(
			() =>
			{
				const term: string =
					this.searchTerm().trim();

				if (term.length === 0)
				{
					return AppConstants.empty;
				}

				if (term.length
					< AppConstants.search.minimumTermLength)
				{
					return AppConstants.search.termTooShortMessage;
				}

				return AppConstants.empty;
			});

	/**
	 * The HTML content for the Quick Find tooltip.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly quickFindTooltip: string =
		'<div class="quick-find-tooltip-content">'
		+ '<p><strong>Use these options to fine-tune your'
		+ ' search results.</strong></p>'
		+ '<p>Find items containing a specific word or add quotes'
		+ ' to find an exact phrase:</p>'
		+ '<p>Example: Smith</p>'
		+ '<ul>'
		+ '<li>Returns all items containing the word Smith</li>'
		+ '</ul>'
		+ '<p>Example: &ldquo;John Smith&rdquo;</p>'
		+ '<ul>'
		+ '<li>Returns only items including the combination'
		+ ' John Smith</li>'
		+ '</ul>'
		+ '<p>Use a minus sign to exclude search results containing'
		+ ' certain words or phrases:</p>'
		+ '<p>Example: -Cancelled</p>'
		+ '<ul>'
		+ '<li>Excludes items containing the word Cancelled</li>'
		+ '</ul>'
		+ '<p>Example: -&ldquo;Pending Approval&rdquo;</p>'
		+ '<ul>'
		+ '<li>Excludes items containing the phrase'
		+ ' Pending Approval</li>'
		+ '</ul>'
		+ '<p>Combined Example:</p>'
		+ '<p>&ldquo;New Business&rdquo; -Cancelled</p>'
		+ '<ul>'
		+ '<li>Returns results that contain the exact phrase'
		+ ' New Business but exclude anything containing'
		+ ' Cancelled.</li>'
		+ '</ul>'
		+ '</div>';

	/**
	 * The flat list of search result DTOs returned by the last
	 * search or load-more call.
	 *
	 * @type {WritableSignal<IInsuranceSearchResultDto[]>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly results:
		WritableSignal<IInsuranceSearchResultDto[]> =
			signal<IInsuranceSearchResultDto[]>([]);

	/**
	 * Paging state keyed by group display label, tracking the
	 * last-seen Id, whether more pages exist, and loading state.
	 *
	 * @type {WritableSignal<Map<string, IGroupPagingState>>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly groupPagingState:
		WritableSignal<Map<string, IGroupPagingState>> =
			signal(new Map());

	/**
	 * The available filter group options rendered in the filter
	 * toolbar, mapping display labels to API group keys.
	 *
	 * @type {{ filterLabel: string; groupKey: string }[]}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly searchGroupOptions:
		{ filterLabel: string; groupKey: string }[] =
			[
				{ filterLabel: 'Policy', groupKey: 'policyTermTransactions' },
				{ filterLabel: 'Account', groupKey: 'accounts' },
				{ filterLabel: 'Claim', groupKey: 'claims' },
				{ filterLabel: 'Agency', groupKey: 'agencies' }
			];

	/**
	 * The set of filter labels for groups included in the next
	 * search. Populated from user permissions during init.
	 *
	 * @type {WritableSignal<Set<string>>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly enabledSearchGroups:
		WritableSignal<Set<string>> =
			signal<Set<string>>(new Set<string>());

	/**
	 * Snapshot of the enabled search groups captured at the time
	 * a search was executed. Used by groupedDisplayResults so that
	 * toggling filters after a search does not reshape the results.
	 *
	 * @type {WritableSignal<Set<string>>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly queriedSearchGroups:
		WritableSignal<Set<string>> =
			signal<Set<string>>(new Set<string>());

	/**
	 * The set of filter labels the user has read-level access to.
	 * Null while permissions are loading.
	 *
	 * @type {WritableSignal<Set<string> | null>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly allowedSearchGroups:
		WritableSignal<Set<string> | null> =
			signal<Set<string> | null>(null);

	/**
	 * The filter group options visible in the toolbar, restricted
	 * to groups the user has read-level permissions for. Empty
	 * while permissions have not yet loaded.
	 *
	 * @type {Signal<{ filterLabel: string; groupKey: string }[]>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly visibleSearchGroupOptions:
		Signal<{ filterLabel: string; groupKey: string }[]> =
			computed(
				() =>
				{
					const allowed: Set<string> | null =
						this.allowedSearchGroups();

					if (AnyHelper.isNull(allowed))
					{
						return [];
					}

					return this.searchGroupOptions.filter(
						(option) =>
							allowed.has(option.filterLabel));
				});

	/**
	 * The set of group display-label keys that are currently
	 * expanded in the results list.
	 *
	 * @type {WritableSignal<Set<string>>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly expandedGroups: WritableSignal<Set<string>> =
		signal<Set<string>>(new Set());

	/**
	 * JS-calculated pixel heights for each expanded group's
	 * scrollable content host, keyed by group display label.
	 *
	 * @type {WritableSignal<Map<string, number>>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly groupContentHeights:
		WritableSignal<Map<string, number>> =
			signal<Map<string, number>>(new Map());

	/**
	 * The set of result IDs whose detail expansion panel is
	 * currently open.
	 *
	 * @type {WritableSignal<Set<number>>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly expandedResultIds:
		WritableSignal<Set<number>> =
			signal<Set<number>>(new Set());

	/**
	 * Non-negated search terms parsed from the last query,
	 * used to highlight matched text in result cards.
	 *
	 * @type {WritableSignal<string[]>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly highlightTerms: WritableSignal<string[]> =
		signal<string[]>([]);

	/**
	 * Whether every currently displayed result group is expanded.
	 * Returns false when no groups are present.
	 *
	 * @type {Signal<boolean>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly allGroupsExpanded: Signal<boolean> =
		computed(
			() =>
			{
				const groups: IInsuranceSearchGroupDisplay[] =
					this.groupedDisplayResults()
						.filter(
							(group: IInsuranceSearchGroupDisplay) =>
								group.items.length > 0);

				if (groups.length === 0)
				{
					return false;
				}

				const expanded: Set<string> =
					this.expandedGroups();

				return groups.every(
					(group: IInsuranceSearchGroupDisplay) =>
						expanded.has(group.groupKey));
			});

	/**
	 * The pre-computed display model for all results, grouped
	 * by entity type and sorted in canonical display order.
	 * Rebuilt once per signal change so template rendering
	 * stays side-effect-free.
	 *
	 * @type {Signal<IInsuranceSearchGroupDisplay[]>}
	 * @memberof InsuranceSearchComponent
	 */
	public readonly groupedDisplayResults:
		Signal<IInsuranceSearchGroupDisplay[]> =
			computed(
				() =>
				{
					const grouped: Map<string, IInsuranceSearchGroupDisplay> =
						new Map();
					const pagingState: Map<string, IGroupPagingState> =
						this.groupPagingState();
					const enabled: Set<string> =
						this.queriedSearchGroups();

					if (this.hasSearched())
					{
						for (const option of this.searchGroupOptions)
						{
							if (!enabled.has(option.filterLabel))
							{
								continue;
							}

							const config:
								IInsuranceSearchGroupDisplayConfig | undefined =
								Object.values(
									InsuranceSearchComponent.displayConfig)
									.find(
										(configuration: any) =>
											configuration.filterLabel ===
												option.filterLabel);

							if (AnyHelper.isNull(config))
							{
								continue;
							}

							const key: string =
								config.displayLabel;
							const state: IGroupPagingState | undefined =
								pagingState.get(key);

							grouped.set(
								key,
								{
									groupKey: key,
									displayLabel: config.displayLabel,
									items: [],
									displayItems: [],
									config,
									lastId: state?.lastId,
									hasMore: state?.hasMore ?? false,
									loading: state?.loading ?? false,
									loadError: state?.loadError ?? false
								});
						}
					}

					for (const result of this.results())
					{
						const config: IInsuranceSearchGroupDisplayConfig =
							this.getDisplayConfig(
								result.entityTypeGroup);
						const key: string =
							config.displayLabel;

						if (!grouped.has(key))
						{
							const state: IGroupPagingState | undefined =
								pagingState.get(key);

							grouped.set(
								key,
								{
									groupKey: key,
									displayLabel: config.displayLabel,
									items: [],
									displayItems: [],
									config,
									lastId: state?.lastId,
									hasMore: state?.hasMore ?? false,
									loading: state?.loading ?? false,
									loadError: state?.loadError ?? false
								});
						}

						const group: IInsuranceSearchGroupDisplay =
							grouped.get(key);
						group.items.push(result);
						group.displayItems.push(
							this.buildResultDisplay(
								result,
								config));
					}

					return Array.from(grouped.values()).sort(
						(groupA, groupB) =>
							InsuranceSearchComponent.groupDisplayOrder
								.indexOf(groupA.displayLabel)
								- InsuranceSearchComponent.groupDisplayOrder
									.indexOf(groupB.displayLabel));
				});

	/**
	 * Gets the search action and group configuration used to
	 * build batch search requests.
	 *
	 * @type {typeof InsuranceSearchComponent.searchConfig}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly searchConfig:
		{
			actionName: string;
			groups:
				{
					accounts: IInsuranceSearchRequest;
					claims: IInsuranceSearchRequest;
					agencies: IInsuranceSearchRequest;
					policyTermTransactions: IInsuranceSearchRequest;
				};
		} = {
			actionName: 'InsuranceSearch',
			groups:
				{
					accounts:
						{
							entityTypeGroup: 'Accounts',
							limit: 20,
							current: 0,
							orderBy: 'Id desc'
						},
					claims:
						{
							entityTypeGroup: 'Claims',
							limit: 20,
							current: 0,
							orderBy: 'Id desc'
						},
					agencies:
						{
							entityTypeGroup: 'Organization.Agencies',
							limit: 20,
							current: 0,
							orderBy: 'Id desc'
						},
					policyTermTransactions:
						{
							entityTypeGroup: 'PolicyTermTransaction.*',
							limit: 20,
							current: 0,
							orderBy: 'Id desc'
						}
				}
		};

	/** Filter label for the Account group.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly filterLabelAccount: string = 'Account';

	/** Filter label for the Claim group.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly filterLabelClaim: string = 'Claim';

	/** Filter label for the Agency group.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly filterLabelAgency: string = 'Agency';

	/** Filter label for the Policy group.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly filterLabelPolicy: string = 'Policy';

	/** Entity type name for the Account entity type.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly entityTypeAccount: string = 'Account';

	/** Entity type name for the Claim entity type.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly entityTypeClaim: string = 'Claim';

	/** Entity type name for the Agency entity type.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly entityTypeAgency: string =
		'Organization.Agency';

	/** Display label for the Accounts result group.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly displayLabelAccounts: string = 'Accounts';

	/** Display label for the Claims result group.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly displayLabelClaims: string = 'Claims';

	/** Display label for the Agencies result group.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly displayLabelAgencies: string = 'Agencies';

	/** Display label for the Policies result group.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly displayLabelPolicies: string = 'Policies';

	/**
	 * Gets the display configuration for a given entity type group, used
	 * to build display models for results and group headers.
	 *
	 * @type {Record<string, IInsuranceSearchGroupDisplayConfig>}
	 * @memberof InsuranceSearchComponent
	*/
	private static readonly displayConfig:
		Record<string, IInsuranceSearchGroupDisplayConfig> =
			{
				PolicyTermTransaction:
					{
						displayLabel:
							InsuranceSearchComponent.displayLabelPolicies,
						filterLabel: InsuranceSearchComponent.filterLabelPolicy,
						titleNameKeys:
							{
								firstName: 'Primary Insured First Name',
								lastName: 'Primary Insured Last Name',
								legalName: 'Primary Insured Legal Name'
							},
						addressSynthesisKeys:
							[
								{
									address: 'Location Address',
									city: 'Location City',
									state: 'Location State'
								},
								{
									address: 'Dwelling Address',
									city: 'Dwelling City',
									state: 'Dwelling State'
								}
							],
						statusKeyword: 'Term Status',
						statusColorMap:
							{
								'Active': 'info',
								'Pending': 'warning',
								'Cancelled': 'error',
								'Expired': 'default',
								'Archived': 'default'
							},
						iconMap:
							{
								'Product': 'fa fa-shield',
								'Policy Number': 'fa fa-file-text-o',
								'Effective Date': 'fa fa-calendar-o'
							},
						fieldOrder:
							[
								'Policy Number',
								'Product',
								'Effective Date'
							],
						excludeKeywords:
							[
								'Status'
							],
						expandedFieldOrder:
							[
								'Type'
							],
						labelMap:
							{
								'Product': 'Policy Type',
								'Type': 'Transaction Type'
							},
						requiredDetailFields:
							[
								'Phone',
								'Email'
							],
						moduleRoute: 'policy/entities',
						expandable: true,
						showFieldIcons: true
					},
				Claims:
					{
						displayLabel:
							InsuranceSearchComponent.displayLabelClaims,
						filterLabel: InsuranceSearchComponent.filterLabelClaim,
						titleKeyword: 'Claim Number',
						statusKeyword: 'Status',
						statusColorMap:
							{
								'Open': 'info',
								'Reported': 'warning',
								'Closed': 'default'
							},
						iconMap:
							{
								'Claim Number': 'fa fa-file-text-o',
								'Policy Number': 'fa fa-file-text-o',
								'Insured Name': 'fa fa-user',
								'Claimant Name': 'fa fa-street-view',
								'Reporter Name': 'fa fa-bullhorn',
								'Cause of Loss': 'fa fa-bolt',
								'Loss Date': 'fa fa-calendar-o'
							},
						fieldOrder:
							[
								'Policy Number',
								'Insured Name',
								'Claimant Name',
								'Reporter Name',
								'Cause of Loss',
								'Loss Date'
							],
						columnKeyMap:
							{
								'Cause of Loss':
									'Loss Cause Description',
								'Claimant Name':
									'Claimant Name'
							},
						labelMap:
							{
								'Insured Name': 'Insured',
								'Claimant Name': 'Claimant',
								'Reporter Name': 'Reporter'
							},
						moduleRoute: 'claim/entities',
						showFieldIcons: true
					},
				Accounts:
					{
						displayLabel:
							InsuranceSearchComponent.displayLabelAccounts,
						filterLabel:
							InsuranceSearchComponent.filterLabelAccount,
						titleKeyword: 'Account Name',
						iconMap:
							{
								'Account Name': 'fa fa-user',
								'Account Number': 'fa fa-address-card',
								'Account Type': 'fa fa-briefcase'
							},
						fieldOrder:
							[
								'Account Number',
								'Account Type'
							],
						labelMap:
							{
								'Account Type': 'Type'
							},
						moduleRoute: 'policy/entities',
						showFieldIcons: true
					},
				'Organization.Agencies':
					{
						displayLabel:
							InsuranceSearchComponent.displayLabelAgencies,
						filterLabel: InsuranceSearchComponent.filterLabelAgency,
						titleKeyword: 'Legal Name',
						statusKeyword: 'Agency Status',
						statusColorMap:
							{
								'Active': 'info',
								'Pending': 'warning',
								'Inactive': 'error'
							},
						iconMap:
							{
								'Legal Name': 'fa fa-building',
								'Agency Status': 'fa fa-circle',
								'Primary Contact': 'fa fa-id-badge',
								'Address': 'fa fa-map-marker',
								'Products': 'fa fa-shield',
								'Contact Email': 'fa fa-envelope',
								'Contact Phone': 'fa fa-phone'
							},
						fieldOrder:
							[
								'Primary Contact',
								'Address',
								'Products',
								'Contact Email',
								'Contact Phone'
							],
						columnKeyMap:
							{
								'Agency Status': 'Status',
								'Address': 'Address',
								'City': 'City',
								'State': 'State',
								'Products':
									'Product Settings'
							},
						excludeKeywords:
							[
								'Contact First Name',
								'Contact Last Name',
								'Contact Legal Name'
							],
						labelMap:
							{
								'Contact Email': 'Email',
								'Contact Phone': 'Phone'
							},
						multiValueFields: ['Products'],
						moduleRoute: 'policy/entities',
						showFieldIcons: true
					}
			};

	/** The `orderBy` clause for descending (newest-first) sorts.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly orderByDesc: string = 'Id desc';

	/** Keyword key used to synthesize an agency primary contact.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly primaryContactField: string =
		'Primary Contact';

	/** Keyword key used to synthesize a column-backed address.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly addressField: string = 'Address';

	/** Fallback icon class when no specific icon is configured.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly defaultIcon: string =
		'fa fa-info-circle';

	/** Keyword key for an agency contact's first name.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly contactFirstNameKey: string =
		'Contact First Name';

	/** Keyword key for an agency contact's last name.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly contactLastNameKey: string =
		'Contact Last Name';

	/** Keyword key for an agency contact's legal name.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly contactLegalNameKey: string =
		'Contact Legal Name';

	/** Entity type group prefix identifying policy term transactions.
	 *
	 * @type {string}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly policyTermTransactionPrefix:
		string = 'PolicyTermTransaction';

	/** Canonical left-to-right display order of result group labels.
	 *
	 * @type {string[]}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly groupDisplayOrder: string[] =
		[
			InsuranceSearchComponent.displayLabelPolicies,
			InsuranceSearchComponent.displayLabelAccounts,
			InsuranceSearchComponent.displayLabelClaims,
			InsuranceSearchComponent.displayLabelAgencies
		];

	/**
	 * The combined top and bottom padding of `.group-content-scroll-host`
	 * (SCSS: `padding: 4px`, so 4 + 4 = 8 px). Used when computing the
	 * natural height of a group's scrollable content area.
	 *
	 * @type {number}
	 * @memberof InsuranceSearchComponent
	 */
	private static readonly scrollHostVerticalPaddingHeight: number = 8;

	/**
	 * Reference to the search input element, used to set
	 * focus on open and after clearing.
	 *
	 * @type {Signal<ElementRef<HTMLInputElement> | undefined>}
	 * @memberof InsuranceSearchComponent
	 */
	private readonly searchInput:
		Signal<ElementRef<HTMLInputElement> | undefined> =
			viewChild<ElementRef<HTMLInputElement>>(
				'searchInput');

	/**
	 * Emits whenever a height recalculation is requested.
	 * Debounced in the constructor to coalesce rapid calls.
	 *
	 * @type {Subject<void>}
	 * @memberof InsuranceSearchComponent
	 */
	private readonly heightRecalculationSubject: Subject<void> =
		new Subject<void>();

	/**
	 * The subscription for the currently active search observable.
	 * Null when no search is in flight.
	 *
	 * @type {Subscription | null}
	 * @memberof InsuranceSearchComponent
	 */
	private activeSearchSubscription: Subscription | null = null;

	/**
	 * Monotonically incrementing counter used to discard stale
	 * responses when a new search supersedes an in-flight one.
	 *
	 * @type {number}
	 * @memberof InsuranceSearchComponent
	 */
	private searchRequestSequence: number = 0;

	/**
	 * Handles the site layout change event.
	 * This will recalculate group content heights when the viewport
	 * or pinned-drawer count changes.
	 *
	 * @memberof InsuranceSearchComponent
	 */
	@HostListener(
		AppEventConstants.siteLayoutChangedEvent)
	public siteLayoutChanged(): void
	{
		this.scheduleHeightRecalculation();
	}

	/**
	 * Initializes the component by setting the entity instance type
	 * group for the API service and loading user permissions to
	 * determine which search groups to show.
	 * Subscriptions are set to auto-cleanup on destroy using the
	 * `takeUntilDestroyed` operator with the component's `DestroyRef`.
	 *
	 * @memberof InsuranceSearchComponent
	 */
	public ngOnInit(): void
	{
		this.loadUserPermissions();
	}

	/**
	 * Clears the search term, results, and search state, and returns
	 * focus to the input.
	 *
	 * @memberof InsuranceSearchComponent
	 */
	public clearSearch(): void
	{
		this.searchTerm.set(AppConstants.empty);
		this.validationMessage.set(AppConstants.empty);
		this.results.set([]);
		this.hasSearched.set(false);
		this.initialSearchError.set(false);
		this.queriedSearchGroups.set(new Set());
		this.searchInput()?.nativeElement.focus();
	}

	/**
	 * Executes the InsuranceSearch workflow action using the current
	 * search term and the configured group requests.
	 *
	 * @memberof InsuranceSearchComponent
	 */
	public search(): void
	{
		const trimmedSearchTerm: string =
			this.searchTerm().trim();

		if (!this.validateSearchTerm(trimmedSearchTerm))
		{
			return;
		}

		this.loading.set(true);
		this.queriedSearchGroups.set(
			new Set(this.enabledSearchGroups()));

		const body: IInsuranceSearchRequest[] =
			this.buildSearchRequestBody(
				InsuranceSearchComponent.orderByDesc);

		if (body.length === 0)
		{
			this.loading.set(false);

			return;
		}

		this.executeSearch(
			body,
			trimmedSearchTerm);
	}

	/**
	 * Navigates to the entity detail page for a selected result.
	 *
	 * @param {IInsuranceSearchResultDto} result
	 * The selected search result.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration for the result's group.
	 * @memberof InsuranceSearchComponent
	 */
	public navigateTo(
		result: IInsuranceSearchResultDto,
		config: IInsuranceSearchGroupDisplayConfig): void
	{
		this.router.navigate(
			[
				config.moduleRoute,
				result.primaryNavigationEntityTypeGroup,
				AppConstants.viewTypes.edit,
				result.primaryNavigationId
			],
			{
				queryParams:
				{
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants
										.layoutTypes
										.full
							})
				}
			});
	}

	/**
	 * Clears the load-error flag for a group and retries the load-more
	 * request.
	 *
	 * @param {IInsuranceSearchResultGroup} group
	 * The group for which to retry loading more results.
	 * @param {HTMLDivElement} groupContentElement
	 * A child element within the group's scrollable container.
	 * @memberof InsuranceSearchComponent
	 */
	public async retryLoadMore(
		group: IInsuranceSearchResultGroup,
		groupContentElement: HTMLDivElement): Promise<void>
	{
		this.setGroupLoadError(
			group.groupKey,
			false);

		await this.loadMore(
			group,
			groupContentElement);
	}

	/**
	 * Toggles the active state of a search filter group. Adding it
	 * when absent, removing it when present.
	 *
	 * @param {string} filterLabel
	 * The filter label to toggle (e.g. 'Policy', 'Claim').
	 * @memberof InsuranceSearchComponent
	 */
	public toggleFilter(filterLabel: string): void
	{
		const current: Set<string> =
			new Set(
				this.enabledSearchGroups());

		if (current.has(filterLabel))
		{
			current.delete(filterLabel);
		}
		else
		{
			current.add(filterLabel);
		}

		this.enabledSearchGroups.set(
			current);
	}

	/**
	 * Returns whether a filter group is currently active.
	 *
	 * @param {string} filterLabel
	 * The filter label to check.
	 * @returns {boolean}
	 * @memberof InsuranceSearchComponent
	 */
	public isFilterActive(filterLabel: string): boolean
	{
		return this.enabledSearchGroups()
			.has(filterLabel);
	}

	/**
	 * Toggles the expanded state of a result group by key.
	 * Triggers a height recalculation after the state change.
	 *
	 * @param {string} groupKey
	 * The display-label key of the group to toggle.
	 * @memberof InsuranceSearchComponent
	 */
	public toggleGroup(groupKey: string): void
	{
		const current: Set<string> =
			new Set(this.expandedGroups());

		if (current.has(groupKey))
		{
			current.delete(groupKey);
		}
		else
		{
			current.add(groupKey);
		}

		this.expandedGroups.set(current);
		this.scheduleHeightRecalculation();
	}

	/**
	 * Returns whether a result group is currently expanded.
	 *
	 * @param {string} groupKey
	 * The display-label key of the group.
	 * @returns {boolean}
	 * @memberof InsuranceSearchComponent
	 */
	public isGroupExpanded(groupKey: string): boolean
	{
		return this.expandedGroups()
			.has(groupKey);
	}

	/**
	 * Expands all result groups when any are collapsed,
	 * or collapses all groups when every group is already expanded.
	 * Triggers a height recalculation after the state change.
	 *
	 * @memberof InsuranceSearchComponent
	 */
	public toggleAllGroups(): void
	{
		if (this.allGroupsExpanded())
		{
			this.expandedGroups.set(new Set());
		}
		else
		{
			const groups: IInsuranceSearchGroupDisplay[] =
				this.groupedDisplayResults()
					.filter(
						(group: IInsuranceSearchGroupDisplay) =>
							group.items.length > 0);

			this.expandedGroups.set(
				new Set(
					groups.map(
						(group: IInsuranceSearchGroupDisplay) =>
							group.groupKey)));
		}

		this.scheduleHeightRecalculation();
	}

	/**
	 * Returns the JS-calculated pixel height for a group's scrollable
	 * content host, or null when no height has been calculated yet
	 * (letting CSS flex take over for the initial render frame).
	 *
	 * @param {string} groupKey
	 * The group display key.
	 * @returns {number | null}
	 * @memberof InsuranceSearchComponent
	 */
	public getGroupScrollHeight(groupKey: string): number | null
	{
		const height: number | undefined =
			this.groupContentHeights()
				.get(groupKey);

		return height ?? null;
	}

	/**
	 * Loads the next page of results for a group and appends them
	 * to the current result list. Restores the previous scroll
	 * position after the DOM updates.
	 *
	 * @param {IInsuranceSearchResultGroup} group
	 * The group for which to load more results.
	 * @param {HTMLDivElement} groupContentElement
	 * A child element within the group's scrollable container,
	 * used to locate the scroll host for position restoration.
	 * @memberof InsuranceSearchComponent
	 */
	public async loadMore(
		group: IInsuranceSearchResultGroup,
		groupContentElement: HTMLDivElement): Promise<void>
	{
		const liveState: IGroupPagingState | undefined =
			this.groupPagingState()
				.get(group.groupKey);

		if (group.loading
			|| !group.hasMore
			|| AnyHelper.isNull(liveState?.lastId)
			|| liveState.lastId === 0)
		{
			return;
		}

		const scrollableContainer: HTMLElement =
			groupContentElement.closest<HTMLElement>(
				'.group-content-scroll-host')
				?? groupContentElement;

		const previousScrollTop: number =
			scrollableContainer.scrollTop;

		this.setGroupLoading(
			group.groupKey,
			true);

		const requestConfig: IInsuranceSearchRequest | undefined =
			this.getRequestConfigForGroup(
				group.groupKey);

		if (!requestConfig)
		{
			this.setGroupLoading(
				group.groupKey,
				false);

			return;
		}

		const body: IInsuranceSearchRequest[] =
			this.buildLoadMoreRequestBody(
				group,
				requestConfig,
				InsuranceSearchComponent.orderByDesc,
				liveState.lastId);

		await this.executeLoadMore(
			group,
			body,
			requestConfig,
			scrollableContainer,
			previousScrollTop);
	}

	/**
	 * Toggles the expanded detail panel for a single result card.
	 * Stops the event from propagating to the card navigation handler.
	 *
	 * @param {IInsuranceSearchResultDto} result
	 * The result whose detail panel should be toggled.
	 * @param {Event} event
	 * The originating DOM event, stopped to prevent navigation.
	 * @memberof InsuranceSearchComponent
	 */
	public toggleResultDetail(
		result: IInsuranceSearchResultDto,
		event: Event): void
	{
		event.stopPropagation();
		const current: Set<number> =
			new Set(this.expandedResultIds());

		if (current.has(result.id))
		{
			current.delete(result.id);
		}
		else
		{
			current.add(result.id);
		}

		this.expandedResultIds.set(
			current);

		afterNextRender(
			() =>
			{
				this.recalculateGroupHeights();
			},
			{
				injector: this.injector
			});
	}

	/**
	 * Returns whether the detail panel of a result card is expanded.
	 *
	 * @param {IInsuranceSearchResultDto} result
	 * The result to check.
	 * @returns {boolean}
	 * @memberof InsuranceSearchComponent
	 */
	public isResultExpanded(
		result: IInsuranceSearchResultDto): boolean
	{
		return this.expandedResultIds()
			.has(result.id);
	}

	/**
	 * Gets the icon class for a keyword key from the config.
	 *
	 * @param {string} key
	 * The keyword key.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration.
	 * @returns {string}
	 * @memberof InsuranceSearchComponent
	 */
	public getFieldIcon(
		key: string,
		config: IInsuranceSearchGroupDisplayConfig): string
	{
		return config.iconMap[key]
			?? InsuranceSearchComponent.defaultIcon;
	}

	/**
	 * Gets highlighted HTML for a display value.
	 *
	 * @param {(string | Date)} value
	 * The raw value to format and highlight.
	 * @returns {string}
	 * @memberof InsuranceSearchComponent
	 */
	public getHighlightedValue(
		value: string | Date): string
	{
		const formattedValue: string =
			this.formatValue(value);

		return this.highlightMatchedTerms(
			formattedValue);
	}

	/**
	 * Formats a keyword value for display.
	 *
	 * @param {string} value
	 * The raw value string.
	 * @returns {string}
	 * @memberof InsuranceSearchComponent
	 */
	public formatValue(value: string | Date): string
	{
		if (AnyHelper.isNull(value))
		{
			return AppConstants.empty;
		}

		const normalizedValue: string =
			value instanceof Date
				? value.toISOString()
				: value;

		if (AnyHelper.isNullOrWhitespace(
			normalizedValue))
		{
			return AppConstants.empty;
		}

		const trimmedValue: string =
			normalizedValue.trim();

		if (this.isIsoDateValue(
			trimmedValue))
		{
			return StringHelper.format(
				trimmedValue,
				AppConstants.formatTypes.shortDate);
		}

		return trimmedValue;
	}

	/**
	 * Resolves the display configuration for an entity type group.
	 *
	 * @param {string} entityTypeGroup
	 * The entity type group string from the result.
	 * @returns {IInsuranceSearchGroupDisplayConfig}
	 * @memberof InsuranceSearchComponent
	 */
	public getDisplayConfig(
		entityTypeGroup: string): IInsuranceSearchGroupDisplayConfig
	{
		if (entityTypeGroup.startsWith(
			InsuranceSearchComponent
				.policyTermTransactionPrefix))
		{
			return InsuranceSearchComponent
				.displayConfig.PolicyTermTransaction;
		}

		return InsuranceSearchComponent
			.displayConfig[entityTypeGroup]
			?? InsuranceSearchComponent
				.displayConfig.Accounts;
	}

	/**
	 * Resolves the display title for a search result. Uses name
	 * synthesis when `titleNameKeys` is configured, otherwise
	 * returns the value of the `titleKeyword` entry.
	 *
	 * @private
	 * @param {IInsuranceSearchResultDto} result
	 * The search result.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration for the result's group.
	 * @returns {string}
	 * @memberof InsuranceSearchComponent
	 */
	private getResultTitle(
		result: IInsuranceSearchResultDto,
		config: IInsuranceSearchGroupDisplayConfig): string
	{
		if (config.titleNameKeys)
		{
			const firstNameEntry: IInsuranceSearchEntry | undefined =
				result.keywords.find(
					(keyword: IInsuranceSearchEntry) =>
						keyword.key ===
							config.titleNameKeys.firstName);
			const lastNameEntry: IInsuranceSearchEntry | undefined =
				result.keywords.find(
					(keyword: IInsuranceSearchEntry) =>
						keyword.key ===
							config.titleNameKeys.lastName);
			const legalNameEntry: IInsuranceSearchEntry | undefined =
				config.titleNameKeys.legalName
					? result.keywords.find(
						(keyword: IInsuranceSearchEntry) =>
							keyword.key ===
								config.titleNameKeys.legalName)
					: undefined;

			const firstName: string =
				firstNameEntry?.values?.[0]
					?? null;
			const lastName: string =
				lastNameEntry?.values?.[0]
					?? null;
			const legalName: string =
				legalNameEntry?.values?.[0]
					?? null;

			return StringHelper.toNameString(
				firstName,
				lastName,
				legalName);
		}

		if (AnyHelper.isNullOrWhitespace(
			config.titleKeyword))
		{
			return AppConstants.empty;
		}

		const titleEntry: IInsuranceSearchEntry | undefined =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key === config.titleKeyword);

		return titleEntry?.values?.[0]
			?? AppConstants.empty;
	}

	/**
	 * Resolves the status badge text for a search result, checking
	 * keywords first and falling back to the configured column path.
	 *
	 * @private
	 * @param {IInsuranceSearchResultDto} result
	 * The search result.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration for the result's group.
	 * @returns {string}
	 * @memberof InsuranceSearchComponent
	 */
	private getResultStatus(
		result: IInsuranceSearchResultDto,
		config: IInsuranceSearchGroupDisplayConfig): string
	{
		if (AnyHelper.isNull(config.statusKeyword))
		{
			return AppConstants.empty;
		}

		const statusEntry: IInsuranceSearchEntry | undefined =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key === config.statusKeyword);
		const keywordStatus: string =
			statusEntry?.values?.[0]
				?? AppConstants.empty;

		if (!AnyHelper.isNullOrWhitespace(keywordStatus))
		{
			return keywordStatus;
		}

		const columnPath: string | undefined =
			config.columnKeyMap?.[config.statusKeyword];

		if (columnPath)
		{
			const columnEntry: IInsuranceSearchEntry | undefined =
				result.columns?.find(
					(column: IInsuranceSearchEntry) =>
						column.key === columnPath);
			const columnStatus: string =
				columnEntry?.values?.[0]
					?? AppConstants.empty;

			if (!AnyHelper.isNullOrWhitespace(columnStatus))
			{
				return columnStatus;
			}
		}

		return AppConstants.empty;
	}

	/**
	 * Gets the ordered keyword fields to display in the card body.
	 *
	 * @param {IInsuranceSearchResultDto} result
	 * The search result.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration.
	 * @returns {IInsuranceSearchEntry[]}
	 * @memberof InsuranceSearchComponent
	 */
	private getDisplayFields(
		result: IInsuranceSearchResultDto,
		config: IInsuranceSearchGroupDisplayConfig): IInsuranceSearchEntry[]
	{
		const excludeKeys: Set<string> =
			new Set(
				[
					config.titleKeyword ?? AppConstants.empty,
					config.statusKeyword ?? AppConstants.empty
				]);

		const fields: IInsuranceSearchEntry[] =
			config.fieldOrder
				.filter(
					(fieldKey: string) =>
						!excludeKeys.has(fieldKey))
				.map(
					(fieldKey: string) =>
						this.resolveDisplayField(
							result,
							config,
							fieldKey));

		if (config.expandable === false)
		{
			const primaryKeys: Set<string> =
				new Set(
					[
						config.titleKeyword ?? AppConstants.empty,
						config.statusKeyword ?? AppConstants.empty,
						...config.fieldOrder
					].map(
						(key: string) =>
							key.toLowerCase()));

			if (config.excludeKeywords)
			{
				for (const key of config.excludeKeywords)
				{
					primaryKeys.add(
						key.toLowerCase());
				}
			}

			const additional: IInsuranceSearchEntry[] =
				result.keywords
					.filter(
						(keyword: IInsuranceSearchEntry) =>
							!primaryKeys.has(
								keyword.key.toLowerCase()))
					.map(
						(keyword: IInsuranceSearchEntry) =>
							this.normalizeSearchEntry(keyword))
					.filter(
						(entry): entry is IInsuranceSearchEntry =>
							!AnyHelper.isNull(
								entry));

			fields.push(
				...additional);
		}

		return fields;
	}

	/**
	 * Gets additional keyword fields not shown in the primary
	 * card display, for the detail expansion view.
	 *
	 * @param {IInsuranceSearchResultDto} result
	 * The search result.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration.
	 * @returns {IInsuranceSearchEntry[]}
	 * @memberof InsuranceSearchComponent
	 */
	private getDetailFields(
		result: IInsuranceSearchResultDto,
		config: IInsuranceSearchGroupDisplayConfig): IInsuranceSearchEntry[]
	{
		const primaryKeys: Set<string> =
			new Set(
				[
					config.titleKeyword ?? AppConstants.empty,
					config.statusKeyword ?? AppConstants.empty,
					...config.fieldOrder
				]);

		if (config.titleNameKeys)
		{
			primaryKeys.add(
				config.titleNameKeys.firstName);
			primaryKeys.add(
				config.titleNameKeys.lastName);

			if (!AnyHelper.isNullOrWhitespace(
				config.titleNameKeys.legalName))
			{
				primaryKeys.add(
					config.titleNameKeys.legalName);
			}
		}

		if (config.addressSynthesisKeys)
		{
			for (const synthesisKeys of config.addressSynthesisKeys)
			{
				primaryKeys.add(synthesisKeys.address);
				primaryKeys.add(synthesisKeys.city);
				primaryKeys.add(synthesisKeys.state);
			}
		}

		if (config.excludeKeywords)
		{
			for (const key of config.excludeKeywords)
			{
				primaryKeys.add(key);
			}
		}

		const fields: IInsuranceSearchEntry[] =
			result.keywords
				.filter(
					(keyword: IInsuranceSearchEntry) =>
						!primaryKeys.has(keyword.key))
				.map(
					(keyword: IInsuranceSearchEntry) =>
						this.normalizeDetailEntry(keyword));

		if (config.addressSynthesisKeys)
		{
			const synthesized: IInsuranceSearchEntry[] =
				config.addressSynthesisKeys
					.map(
						(synthesisKeys) =>
							this.buildSynthesizedAddress(
								result, synthesisKeys))
					.filter(
						(entry): entry is IInsuranceSearchEntry =>
							!AnyHelper.isNull(entry));

			fields.unshift(...synthesized);
		}

		this.ensureRequiredDetailFields(
			fields,
			config);

		if (config.expandedFieldOrder)
		{
			const orderedKeySet: Set<string> =
				new Set(config.expandedFieldOrder);

			const pinnedFields: IInsuranceSearchEntry[] =
				config.expandedFieldOrder
					.map(
						(key: string) =>
							fields.find(
								(field: IInsuranceSearchEntry) =>
									field.key === key))
					.filter(
						(entry): entry is IInsuranceSearchEntry =>
							!AnyHelper.isNull(entry));

			const remainingFields: IInsuranceSearchEntry[] =
				fields.filter(
					(field: IInsuranceSearchEntry) =>
						!orderedKeySet.has(field.key));

			fields.splice(
				0,
				fields.length,
				...pinnedFields,
				...remainingFields);
		}

		return fields;
	}

	/**
	 * Ensures all keys listed in `config.requiredDetailFields` are
	 * present in the detail fields array. Any key not already
	 * represented is appended with an empty value, preserving a
	 * consistent layout regardless of what keywords the server returns.
	 *
	 * @private
	 * @param {IInsuranceSearchEntry[]} fields
	 * The mutable detail fields array to patch.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration for the group.
	 * @memberof InsuranceSearchComponent
	 */
	private ensureRequiredDetailFields(
		fields: IInsuranceSearchEntry[],
		config: IInsuranceSearchGroupDisplayConfig): void
	{
		if (AnyHelper.isNull(config.requiredDetailFields))
		{
			return;
		}

		const presentKeys: Set<string> =
			new Set(
				fields.map(
					(field: IInsuranceSearchEntry) =>
						field.key));

		for (const key of config.requiredDetailFields)
		{
			if (!presentKeys.has(key))
			{
				fields.push(
					{
						key,
						values: [AppConstants.empty]
					});
			}
		}
	}

	/**
	 * Resolves a single display field from keywords, columns,
	 * or synthesized contact data.
	 *
	 * @param {IInsuranceSearchResultDto} result
	 * The search result.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration.
	 * @param {string} fieldKey
	 * The field key to resolve.
	 * @returns {IInsuranceSearchEntry}
	 * @memberof InsuranceSearchComponent
	 */
	private resolveDisplayField(
		result: IInsuranceSearchResultDto,
		config: IInsuranceSearchGroupDisplayConfig,
		fieldKey: string): IInsuranceSearchEntry
	{
		if (fieldKey ===
			InsuranceSearchComponent.primaryContactField)
		{
			return this.synthesizePrimaryContact(result)
				?? {
					key: fieldKey,
					values: [AppConstants.empty]
				};
		}

		if (fieldKey ===
			InsuranceSearchComponent.addressField)
		{
			return this.synthesizeAddress(result, config)
				?? {
					key: fieldKey,
					values: [AppConstants.empty]
				};
		}

		const keywordEntry: IInsuranceSearchEntry | undefined =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key === fieldKey);

		if (keywordEntry)
		{
			const values: string[] =
				this.resolveFieldValues(
					fieldKey,
					keywordEntry.values,
					config);

			return {
				key: keywordEntry.key,
				values: values.length ? values : [AppConstants.empty]
			};
		}

		const columnPath: string | undefined =
			config.columnKeyMap?.[fieldKey];

		if (columnPath)
		{
			const columnEntry: IInsuranceSearchEntry | undefined =
				result.columns?.find(
					(column: IInsuranceSearchEntry) =>
						column.key === columnPath);

			if (columnEntry)
			{
				const values: string[] =
					this.resolveFieldValues(
						fieldKey,
						columnEntry.values ?? [],
						config);

				return {
					key: fieldKey,
					values: values.length ? values : [AppConstants.empty]
				};
			}
		}

		return {
			key: fieldKey,
			values: [AppConstants.empty]
		};
	}

	/**
	 * Synthesizes an "Address" entry by combining address,
	 * city, and state column values at index 0.
	 *
	 * @param {IInsuranceSearchResultDto} result
	 * The search result.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display config with columnKeyMap.
	 * @returns {IInsuranceSearchEntry | undefined}
	 * @memberof InsuranceSearchComponent
	 */
	private synthesizeAddress(
		result: IInsuranceSearchResultDto,
		config: IInsuranceSearchGroupDisplayConfig):
		IInsuranceSearchEntry | undefined
	{
		const addressPath: string | undefined =
			config.columnKeyMap?.Address;

		const cityPath: string | undefined =
			config.columnKeyMap?.City;

		const statePath: string | undefined =
			config.columnKeyMap?.State;

		const addresses: string[] =
			result.columns?.find(
				(column: IInsuranceSearchEntry) =>
					column.key === addressPath)
				?.values ?? [];

		const cities: string[] =
			result.columns?.find(
				(column: IInsuranceSearchEntry) =>
					column.key === cityPath)
				?.values ?? [];

		const states: string[] =
			result.columns?.find(
				(column: IInsuranceSearchEntry) =>
					column.key === statePath)
				?.values ?? [];

		const maxLength: number =
			Math.max(
				addresses.length,
				cities.length,
				states.length);

		if (maxLength === 0)
		{
			return undefined;
		}

		const formatted: string[] = [];

		for (let index: number = 0;
			index < maxLength;
			index++)
		{
			const line: string =
				this.formatSearchDisplayAddress(
					addresses[index] ?? AppConstants.empty,
					cities[index] ?? AppConstants.empty,
					states[index] ?? AppConstants.empty);

			if (!AnyHelper.isNullOrWhitespace(line))
			{
				formatted.push(line);
			}
		}

		if (formatted.length === 0)
		{
			return undefined;
		}

		return {
			key: InsuranceSearchComponent.addressField,
			values: formatted
		};
	}

	/**
	 * Synthesizes a "Primary Contact" entry from contact
	 * first name and last name keywords at index 0.
	 *
	 * @param {IInsuranceSearchResultDto} result
	 * The search result.
	 * @returns {IInsuranceSearchEntry | undefined}
	 * @memberof InsuranceSearchComponent
	 */
	private synthesizePrimaryContact(
		result: IInsuranceSearchResultDto):
		IInsuranceSearchEntry | undefined
	{
		const legalName: string =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key ===
						InsuranceSearchComponent
							.contactLegalNameKey)
				?.values?.[0] ?? null;

		const firstName: string =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key ===
						InsuranceSearchComponent
							.contactFirstNameKey)
				?.values?.[0] ?? null;

		const lastName: string =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key ===
						InsuranceSearchComponent
							.contactLastNameKey)
				?.values?.[0] ?? null;

		const fullName: string =
			StringHelper.toNameString(
				firstName,
				lastName,
				legalName);

		if (AnyHelper.isNullOrWhitespace(fullName))
		{
			return undefined;
		}

		return {
			key: InsuranceSearchComponent.primaryContactField,
			values: [fullName]
		};
	}

	/**
	 * Schedules a deferred group height recalculation. Emits to the
	 * height recalculation subject, which is debounced so that rapid
	 * successive calls produce only one DOM measurement.
	 *
	 * @private
	 * @memberof InsuranceSearchComponent
	 */
	private scheduleHeightRecalculation(): void
	{
		this.heightRecalculationSubject
			.next();
	}

	/**
	 * Calculates and applies pixel heights to each expanded group's
	 * scrollable content host so that open groups share available
	 * vertical space equally. Groups whose natural content height is
	 * smaller than their equal share donate the surplus to the
	 * remaining groups via an iterative redistribution loop.
	 *
	 * @private
	 * @memberof InsuranceSearchComponent
	 */
	private recalculateGroupHeights(): void
	{
		const containerEl: HTMLElement | null =
			this.elementRef.nativeElement
				.querySelector<HTMLElement>(
					'.results-container');

		if (AnyHelper.isNull(containerEl))
		{
			return;
		}

		const groups: IInsuranceSearchGroupDisplay[] =
			this.groupedDisplayResults();
		const expanded: Set<string> =
			this.expandedGroups();

		if (expanded.size === 0)
		{
			this.groupContentHeights.set(new Map());

			return;
		}

		const containerHeight: number =
			containerEl.clientHeight - 3.5;
		const firstHeader: HTMLElement | null =
			containerEl.querySelector<HTMLElement>(
				'.group-header');
		const headerHeight: number =
			firstHeader?.offsetHeight ?? 37;
		const totalContentHeight: number =
			Math.max(
				0,
				containerHeight - (groups.length * headerHeight));

		const scrollHosts: NodeListOf<HTMLElement> =
			containerEl.querySelectorAll<HTMLElement>(
				'.group-content-scroll-host');

		const naturalHeights: Map<string, number> =
			this.measureNaturalGroupHeights(
				groups,
				expanded,
				scrollHosts);

		const expandedKeys: string[] =
			groups
				.filter(
					(group: IInsuranceSearchGroupDisplay) =>
						expanded.has(group.groupKey))
				.map(
					(group: IInsuranceSearchGroupDisplay) =>
						group.groupKey);

		const heights: Map<string, number> =
			this.redistributeGroupHeights(
				expandedKeys,
				naturalHeights,
				totalContentHeight);

		this.groupContentHeights.set(
			heights);
	}

	/**
	 * Measures the natural (unconstrained) pixel height of each
	 * expanded group's scrollable content by reading the inner
	 * content element's offsetHeight, which is never height-capped
	 * by the JS height binding.
	 *
	 * @private
	 * @param {IInsuranceSearchGroupDisplay[]} groups
	 * All display groups in render order.
	 * @param {Set<string>} expanded
	 * The set of currently expanded group keys.
	 * @param {NodeListOf<HTMLElement>} scrollHosts
	 * The `.group-content-scroll-host` elements in DOM order.
	 * @returns {Map<string, number>}
	 * @memberof InsuranceSearchComponent
	 */
	private measureNaturalGroupHeights(
		groups: IInsuranceSearchGroupDisplay[],
		expanded: Set<string>,
		scrollHosts: NodeListOf<HTMLElement>): Map<string, number>
	{
		const naturalHeights: Map<string, number> = new Map();
		let hostIndex: number = 0;

		for (const group of groups)
		{
			if (expanded.has(group.groupKey))
			{
				const host: HTMLElement =
					scrollHosts[hostIndex];

				if (!AnyHelper.isNull(host))
				{
					const innerContent: HTMLElement =
						host.firstElementChild as HTMLElement;
					naturalHeights.set(
						group.groupKey,
						(innerContent?.offsetHeight ?? 0)
							+ InsuranceSearchComponent
								.scrollHostVerticalPaddingHeight);
				}

				hostIndex++;
			}
		}

		return naturalHeights;
	}

	/**
	 * Distributes `totalContentHeight` across `expandedKeys` using an
	 * iterative equal-split with redistribution: groups whose natural
	 * height is below their equal share take their natural height and
	 * donate the surplus to the remaining groups.
	 *
	 * @private
	 * @param {string[]} expandedKeys
	 * Ordered list of expanded group keys.
	 * @param {Map<string, number>} naturalHeights
	 * Natural content height per group key.
	 * @param {number} totalContentHeight
	 * Total pixels available for all scroll-host content areas.
	 * @returns {Map<string, number>}
	 * @memberof InsuranceSearchComponent
	 */
	private redistributeGroupHeights(
		expandedKeys: string[],
		naturalHeights: Map<string, number>,
		totalContentHeight: number): Map<string, number>
	{
		const heights: Map<string, number> = new Map();
		let remaining: string[] = [...expandedKeys];
		let remainingHeight: number = totalContentHeight;

		while (remaining.length > 0)
		{
			const equalShare: number =
				remainingHeight / remaining.length;
			const resolved: string[] =
				remaining.filter(
					(key: string) =>
						(naturalHeights.get(key) ?? Infinity)
							< equalShare);

			if (resolved.length === 0)
			{
				break;
			}

			for (const key of resolved)
			{
				const natural: number =
					naturalHeights.get(key) ?? 0;
				heights.set(
					key,
					natural);
				remainingHeight -= natural;
			}

			remaining =
				remaining.filter(
					(key: string) =>
						!heights.has(key));
		}

		const remainingCount: number = remaining.length;

		if (remainingCount > 0)
		{
			const share: number =
				Math.max(
					0,
					remainingHeight / remainingCount);

			for (const key of remaining)
			{
				heights.set(
					key,
					share);
			}
		}

		return heights;
	}

	/**
	 * Cancels the currently active search subscription.
	 *
	 * @private
	 * @memberof InsuranceSearchComponent
	 */
	private cancelActiveSearch(): void
	{
		if (!AnyHelper.isNull(this.activeSearchSubscription))
		{
			this.activeSearchSubscription.unsubscribe();
			this.activeSearchSubscription = null;
		}
	}

	/**
	 * Builds the full pre-computed display object for a single
	 * search result, including highlighted title, status, all
	 * field values, and the expandable flag. Called inside the
	 * groupedDisplayResults computed so all work happens once
	 * per signal change rather than per change-detection cycle.
	 *
	 * @private
	 * @param {IInsuranceSearchResultDto} result
	 * The raw search result.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration for the result's group.
	 * @returns {IInsuranceSearchResultDisplay}
	 * @memberof InsuranceSearchComponent
	 */
	private buildResultDisplay(
		result: IInsuranceSearchResultDto,
		config: IInsuranceSearchGroupDisplayConfig):
		IInsuranceSearchResultDisplay
	{
		const title: string =
			this.getResultTitle(
				result,
				config);
		const status: string =
			this.getResultStatus(
				result,
				config);
		const displayEntries: IInsuranceSearchEntry[] =
			this.getDisplayFields(
				result,
				config);
		const detailEntries: IInsuranceSearchEntry[] =
			this.getDetailFields(
				result,
				config);

		return {
			id: result.id,
			result,
			config,
			highlightedTitle:
				this.highlightMatchedTerms(title),
			highlightedStatus:
				this.highlightMatchedTerms(status),
			statusColor:
				config.statusColorMap?.[status]
					?? AppConstants.empty,
			displayFields: displayEntries.map(
				(entry) =>
					this.toFieldDisplay(entry, config)),
			detailFields: detailEntries.map(
				(entry) =>
					this.toFieldDisplay(entry, config)),
			isExpandable:
				config.expandable !== false
					&& detailEntries.length > 0
		};
	}

	/**
	 * Converts a raw IInsuranceSearchEntry into an ISearchFieldDisplay,
	 * resolving the icon and pre-highlighting all values.
	 *
	 * @private
	 * @param {IInsuranceSearchEntry} entry
	 * The field entry to convert.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration used for icon resolution.
	 * @returns {IInsuranceSearchFieldDisplay}
	 * @memberof InsuranceSearchComponent
	 */
	private toFieldDisplay(
		entry: IInsuranceSearchEntry,
		config: IInsuranceSearchGroupDisplayConfig):
		IInsuranceSearchFieldDisplay
	{
		return {
			key: config.labelMap?.[entry.key] ?? entry.key,
			icon: this.getFieldIcon(
				entry.key,
				config),
			highlightedValues:
				entry.values.map(
					(value) =>
						this.getHighlightedValue(value))
		};
	}

	/**
	 * Builds a synthesized address entry from keyword values for a
	 * single addressSynthesisKeys group. Iterates all indices so
	 * that multiple locations (e.g. DP1DC) each produce their own
	 * formatted line. Returns undefined when no address data exists.
	 *
	 * @private
	 * @param {IInsuranceSearchResultDto} result
	 * The search result.
	 * @param {{ address: string; city: string; state: string }} keys
	 * The synthesis key set.
	 * @returns {IInsuranceSearchEntry | undefined}
	 * @memberof InsuranceSearchComponent
	 */
	private buildSynthesizedAddress(
		result: IInsuranceSearchResultDto,
		keys: { address: string; city: string; state: string }):
		IInsuranceSearchEntry | undefined
	{
		const addresses: string[] =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key === keys.address)
				?.values ?? [];

		const cities: string[] =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key === keys.city)
				?.values ?? [];

		const states: string[] =
			result.keywords.find(
				(keyword: IInsuranceSearchEntry) =>
					keyword.key === keys.state)
				?.values ?? [];

		const maxLength: number =
			Math.max(
				addresses.length,
				cities.length,
				states.length);

		if (maxLength === 0)
		{
			return undefined;
		}

		const formatted: string[] = [];

		for (let index: number = 0;
			index < maxLength;
			index++)
		{
			const line: string =
				this.formatSearchDisplayAddress(
					addresses[index] ?? AppConstants.empty,
					cities[index] ?? AppConstants.empty,
					states[index] ?? AppConstants.empty);

			if (!AnyHelper.isNullOrWhitespace(line))
			{
				formatted.push(line);
			}
		}

		if (formatted.length === 0)
		{
			return undefined;
		}

		return {
			key: keys.address,
			values: formatted
		};
	}

	/**
	 * Parses search terms and returns non-negated terms for highlighting.
	 *
	 * @private
	 * @param {string} searchTerm
	 * The search term entered by the user.
	 * @returns {string[]}
	 * @memberof InsuranceSearchComponent
	 */
	private parseHighlightTerms(
		searchTerm: string): string[]
	{
		const seen: Set<string> = new Set<string>();
		const terms: string[] = [];
		const tokenPattern: RegExp =
			/-?"[^"]+"|-\S+|\S+/g;

		for (const match of searchTerm.trim().matchAll(tokenPattern))
		{
			const token: string =
				match[0];

			if (token.startsWith('-'))
			{
				continue;
			}

			const clean: string =
				token.startsWith('"')
					&& token.endsWith('"')
					&& token.length > 2
					? token.slice(1, -1)
					: token;

			if (!AnyHelper.isNullOrWhitespace(clean)
				&& !seen.has(clean.toLowerCase()))
			{
				seen.add(clean.toLowerCase());
				terms.push(clean);
			}
		}

		return terms;
	}

	/**
	 * Applies safe HTML highlighting to matched non-negated search terms.
	 *
	 * @private
	 * @param {string} value
	 * The value to highlight.
	 * @returns {string}
	 * @memberof InsuranceSearchComponent
	 */
	private highlightMatchedTerms(value: string): string
	{
		if (AnyHelper.isNullOrWhitespace(value))
		{
			return AppConstants.empty;
		}

		if (AnyHelper.isNullOrEmpty(this.highlightTerms()))
		{
			return this.escapeHtml(value);
		}

		const termPattern: string =
			this.highlightTerms()
				.map(
					(term) =>
						this.escapeRegExp(term))
				.join('|');

		if (AnyHelper.isNullOrWhitespace(termPattern))
		{
			return this.escapeHtml(value);
		}

		const regex: RegExp =
			new RegExp(`(${termPattern})`, 'gi');
		const segments: string[] =
			value.split(regex);

		return segments
			.map(
				(segment) =>
				{
					if (AnyHelper.isNullOrEmpty(segment))
					{
						return AppConstants.empty;
					}

					const matchedTerm: string | undefined =
						this.highlightTerms().find(
							(term: string) =>
								term.toLowerCase()
									=== segment.toLowerCase());
					const escapedSegment: string =
						this.escapeHtml(
							segment);

					if (!AnyHelper.isNull(matchedTerm))
					{
						return `<span class="search-highlight">${
							escapedSegment}</span>`;
					}

					return escapedSegment;
				})
			.join(AppConstants.empty);
	}

	/**
	 * Escapes HTML entities in a string.
	 *
	 * @private
	 * @param {string} value
	 * The value to escape.
	 * @returns {string}
	 * @memberof InsuranceSearchComponent
	 */
	private escapeHtml(value: string): string
	{
		const apostrophe: string =
			String.fromCodePoint(39);

		return value
			.replaceAll('&', '&amp;')
			.replaceAll('<', '&lt;')
			.replaceAll('>', '&gt;')
			.replaceAll('"', '&quot;')
			.replaceAll(apostrophe, '&#39;');
	}

	/**
	 * Escapes regex special characters.
	 *
	 * @private
	 * @param {string} value
	 * The value to escape.
	 * @returns {string}
	 * @memberof InsuranceSearchComponent
	 */
	private escapeRegExp(value: string): string
	{
		return value.replace(
			/[.*+?^${}()|[\]\\]/g,
			String.raw`\$&`);
	}

	/**
	 * Returns whether a value is a valid ISO 8601 date/time.
	 *
	 * @private
	 * @param {string} value
	 * The value to validate.
	 * @returns {boolean}
	 * @memberof InsuranceSearchComponent
	 */
	private isIsoDateValue(value: string): boolean
	{
		const parsedDate: DateTime =
			DateTime.fromISO(value);

		return parsedDate.isValid;
	}

	/**
	 * Normalizes a search entry to include only non-empty values.
	 *
	 * @private
	 * @param {IInsuranceSearchEntry} entry
	 * The entry to normalize.
	 * @returns {(IInsuranceSearchEntry | undefined)}
	 * @memberof InsuranceSearchComponent
	 */
	private normalizeSearchEntry(
		entry: IInsuranceSearchEntry): IInsuranceSearchEntry | undefined
	{
		const entryValues: string[] =
			entry.values;

		if (entryValues.length === 0)
		{
			return undefined;
		}

		return {
			key: entry.key,
			values: entryValues
		};
	}

	/**
	 * Normalizes a search entry for detail expansion display.
	 * Unlike normalizeSearchEntry, this always returns an entry even
	 * when all values are empty, so keywords with null or empty values
	 * still contribute to the detail panel and allow the card to be
	 * expandable.
	 *
	 * @private
	 * @param {IInsuranceSearchEntry} entry
	 * The search entry keyword to normalize.
	 * @returns {IInsuranceSearchEntry}
	 * @memberof InsuranceSearchComponent
	 */
	private normalizeDetailEntry(
		entry: IInsuranceSearchEntry): IInsuranceSearchEntry
	{
		return {
			key: entry.key,
			values: AnyHelper.isNullOrEmpty(entry.values)
				? [AppConstants.empty]
				: entry.values
		};
	}

	/**
	 * Resolves the display values for a field key, respecting
	 * the `multiValueFields` config: returns all non-empty values
	 * when the field is listed there, otherwise returns only index 0.
	 *
	 * @private
	 * @param {string} fieldKey
	 * The field key being resolved.
	 * @param {string[]} rawValues
	 * The raw values array from the result entry.
	 * @param {IInsuranceSearchGroupDisplayConfig} config
	 * The display configuration for the group.
	 * @returns {string[]}
	 * @memberof InsuranceSearchComponent
	 */
	private resolveFieldValues(
		fieldKey: string,
		rawValues: string[],
		config: IInsuranceSearchGroupDisplayConfig): string[]
	{
		if ((config.multiValueFields ?? []).includes(fieldKey))
		{
			return rawValues;
		}

		return rawValues?.[0]
			? [rawValues[0]]
			: [];
	}

	/**
	 * Updates per-group paging state from a fresh search response.
	 *
	 * @private
	 * @param {IInsuranceSearchResultDto[]} results
	 * The results from the search.
	 * @param {IInsuranceSearchRequest[]} requests
	 * The original requests sent.
	 * @memberof InsuranceSearchComponent
	 */
	private updatePagingState(
		results: IInsuranceSearchResultDto[],
		requests: IInsuranceSearchRequest[]): void
	{
		const newState: Map<string, IGroupPagingState> =
			new Map();

		const limitByGroup: Map<string, number> =
			new Map(
				requests.map(
					(request) =>
						[
							this.getDisplayConfig(
								request.entityTypeGroup)
								.displayLabel,
							request.limit
						]));

		const grouped: Map<string, IInsuranceSearchResultDto[]> =
			new Map();

		for (const result of results)
		{
			const config: IInsuranceSearchGroupDisplayConfig =
				this.getDisplayConfig(
					result.entityTypeGroup);
			const key: string = config.displayLabel;

			if (!grouped.has(key))
			{
				grouped.set(key, []);
			}

			grouped.get(key)
				.push(result);
		}

		for (const [key, items] of grouped)
		{
			const limit: number = limitByGroup.get(key) ?? 20;
			const lastItem: IInsuranceSearchResultDto =
				items.at(-1);

			newState.set(
				key,
				{
					lastId: lastItem?.id,
					hasMore: items.length >= limit,
					loading: false,
					loadError: false
				});
		}

		this.groupPagingState.set(
			newState);
	}

	/**
	 * Updates paging state for a single group after a Load More.
	 *
	 * @private
	 * @param {string} groupKey
	 * The group key that was loaded.
	 * @param {IInsuranceSearchResultDto[]} newResults
	 * The new results from the load more call.
	 * @param {number} limit
	 * The page size limit.
	 * @memberof InsuranceSearchComponent
	 */
	private updateGroupPagingAfterLoadMore(
		groupKey: string,
		newResults: IInsuranceSearchResultDto[],
		limit: number): void
	{
		const current: Map<string, IGroupPagingState> =
			new Map(this.groupPagingState());

		const lastItem: IInsuranceSearchResultDto =
			newResults.at(-1);

		current.set(
			groupKey,
			{
				lastId: lastItem?.id
					?? current.get(groupKey)?.lastId,
				hasMore: newResults.length >= limit,
				loading: false,
				loadError: false
			});

		this.groupPagingState.set(
			current);
	}

	/**
	 * Sets the loading state for a specific group.
	 *
	 * @private
	 * @param {string} groupKey
	 * The group key.
	 * @param {boolean} isLoading
	 * Whether loading is in progress.
	 * @memberof InsuranceSearchComponent
	 */
	private setGroupLoading(
		groupKey: string,
		isLoading: boolean): void
	{
		const current: Map<string, IGroupPagingState> =
			new Map(this.groupPagingState());

		const existing: IGroupPagingState | undefined =
			current.get(groupKey);

		if (!AnyHelper.isNull(existing))
		{
			current.set(
				groupKey,
				{
					...existing,
					loading: isLoading
				});
		}

		this.groupPagingState.set(
			current);
	}

	/**
	 * Sets the load error state for a specific group.
	 *
	 * @private
	 * @param {string} groupKey
	 * The group key.
	 * @param {boolean} hasError
	 * Whether a load-more error is present.
	 * @memberof InsuranceSearchComponent
	 */
	private setGroupLoadError(
		groupKey: string,
		hasError: boolean): void
	{
		const current: Map<string, IGroupPagingState> =
			new Map(this.groupPagingState());

		const existing: IGroupPagingState | undefined =
			current.get(groupKey);

		if (!AnyHelper.isNull(existing))
		{
			current.set(
				groupKey,
				{
					...existing,
					loadError: hasError
				});
		}

		this.groupPagingState.set(
			current);
	}

	/**
	 * Returns the static request config matching a display
	 * group key.
	 *
	 * @private
	 * @param {string} groupKey
	 * The display label group key.
	 * @returns {IInsuranceSearchRequest | undefined}
	 * @memberof InsuranceSearchComponent
	 */
	private getRequestConfigForGroup(
		groupKey: string): IInsuranceSearchRequest | undefined
	{
		const groups: {
			accounts: IInsuranceSearchRequest;
			claims: IInsuranceSearchRequest;
			agencies: IInsuranceSearchRequest;
			policyTermTransactions: IInsuranceSearchRequest;
		} =
			InsuranceSearchComponent.searchConfig.groups;

		switch (groupKey)
		{
			case InsuranceSearchComponent.displayLabelAccounts:
				return groups.accounts;
			case InsuranceSearchComponent.displayLabelClaims:
				return groups.claims;
			case InsuranceSearchComponent.displayLabelAgencies:
				return groups.agencies;
			case InsuranceSearchComponent.displayLabelPolicies:
				return groups.policyTermTransactions;
			default:
				return undefined;
		}
	}

	/**
	 * Returns a formatted address display string combining
	 * address, city, and state. Produces format:
	 * "address, city state". Gracefully handles missing parts.
	 *
	 * @param {string} address
	 * The street address string.
	 * @param {string} city
	 * The city string.
	 * @param {string} state
	 * The state/province abbreviation string.
	 * @returns {string}
	 * A formatted address display string.
	 * @memberof InsuranceSearchComponent
	 */
	private formatSearchDisplayAddress(
		address: string,
		city: string,
		state: string): string
	{
		const hasAddress: boolean =
			!AnyHelper.isNullOrWhitespace(address);
		const hasCity: boolean =
			!AnyHelper.isNullOrWhitespace(city);
		const hasState: boolean =
			!AnyHelper.isNullOrWhitespace(state);

		if (!hasAddress && !hasCity && !hasState)
		{
			return AppConstants.empty;
		}

		let cityState: string = AppConstants.empty;

		if (hasCity && hasState)
		{
			cityState = `${city} ${state}`;
		}
		else if (hasCity)
		{
			cityState = city;
		}
		else if (hasState)
		{
			cityState = state;
		}

		if (!hasAddress)
		{
			return cityState;
		}

		return AnyHelper.isNullOrWhitespace(cityState)
			? address
			: `${address}, ${cityState}`;
	}

	/**
	 * Validates the trimmed search term against the minimum length
	 * requirement. Sets the validation message when invalid and
	 * clears it when valid.
	 *
	 * @private
	 * @param {string} trimmedSearchTerm
	 * The trimmed search term to validate.
	 * @returns {boolean}
	 * True when the term meets the minimum length; false otherwise.
	 * @memberof InsuranceSearchComponent
	 */
	private validateSearchTerm(
		trimmedSearchTerm: string): boolean
	{
		if (trimmedSearchTerm.length
			< AppConstants.search.minimumTermLength)
		{
			this.validationMessage.set(
				AppConstants.search.termTooShortMessage);

			return false;
		}

		this.validationMessage.set(
			AppConstants.empty);

		return true;
	}

	/**
	 * Builds the batch search request body from the currently enabled
	 * filter groups, applying the provided `orderBy` clause to each.
	 *
	 * @private
	 * @param {string} orderBy
	 * The `orderBy` clause to attach to every request entry.
	 * @returns {IInsuranceSearchRequest[]}
	 * An array of request entries for every enabled group,
	 * or an empty array when no groups are enabled.
	 * @memberof InsuranceSearchComponent
	 */
	private buildSearchRequestBody(
		orderBy: string): IInsuranceSearchRequest[]
	{
		const enabled: Set<string> =
			this.enabledSearchGroups();

		const body: IInsuranceSearchRequest[] = [];

		if (enabled.has(
			InsuranceSearchComponent.filterLabelAccount))
		{
			body.push(
				{
					...InsuranceSearchComponent.searchConfig
						.groups.accounts,
					orderBy
				});
		}

		if (enabled.has(
			InsuranceSearchComponent.filterLabelClaim))
		{
			body.push(
				{
					...InsuranceSearchComponent.searchConfig
						.groups.claims,
					orderBy
				});
		}

		if (enabled.has(
			InsuranceSearchComponent.filterLabelAgency))
		{
			body.push(
				{
					...InsuranceSearchComponent.searchConfig
						.groups.agencies,
					orderBy
				});
		}

		if (enabled.has(
			InsuranceSearchComponent.filterLabelPolicy))
		{
			body.push(
				{
					...InsuranceSearchComponent.searchConfig
						.groups.policyTermTransactions,
					orderBy
				});
		}

		return body;
	}

	/**
	 * Cancels any in-flight search, increments the sequence counter,
	 * executes the InsuranceSearch action with the given request body,
	 * and subscribes to the response. Stale responses (from a
	 * superseded sequence) are discarded in each handler.
	 *
	 * @private
	 * @param {IInsuranceSearchRequest[]} body
	 * The batch search request body to send.
	 * @param {string} trimmedSearchTerm
	 * The trimmed search term, used for URL encoding and highlight
	 * term parsing.
	 * @memberof InsuranceSearchComponent
	 */
	private executeSearch(
		body: IInsuranceSearchRequest[],
		trimmedSearchTerm: string): void
	{
		this.cancelActiveSearch();
		this.searchRequestSequence++;
		const currentSequence: number =
			this.searchRequestSequence;
		const parsedHighlightTerms: string[] =
			this.parseHighlightTerms(trimmedSearchTerm);

		const systemId: number =
			Number.parseInt(
				AppConstants.systemId,
				AppConstants.parseRadix);

		this.entityInstanceApiService.entityInstanceTypeGroup =
			AppConstants.typeGroups.systems;

		const searchObservable: Observable<any> =
			from(
				this.entityInstanceApiService
					.executeActionWithPayload<any>(
						systemId,
						InsuranceSearchComponent.searchConfig
							.actionName,
						{ searchRequests: body },
						`?searchTerm=${
							encodeURIComponent(
								trimmedSearchTerm)}`));

		this.activeSearchSubscription =
			searchObservable
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(
					{
						next: (httpResponse: any) =>
						{
							if (currentSequence !== this.searchRequestSequence)
							{
								return;
							}

							const actionResponse: any =
								httpResponse?.body ?? httpResponse;

							const searchResults: IInsuranceSearchResultDto[] =
								actionResponse?.value ?? [];

							this.results.set(
								searchResults);
							this.highlightTerms.set(
								parsedHighlightTerms);
							this.initialSearchError.set(false);
							this.applyAutoExpand();
							this.updatePagingState(
								searchResults,
								body);

							const groups: IInsuranceSearchGroupDisplay[] =
								this.groupedDisplayResults();
							const initialExpanded: Set<string> =
								new Set(
									groups
										.filter(
											(group:
												IInsuranceSearchGroupDisplay) =>
												group.items.length > 0)
										.map(
											(group:
												IInsuranceSearchGroupDisplay) =>
												group.groupKey));
							this.expandedGroups.set(
								initialExpanded);
							this.hasSearched.set(
								true);
							this.loading.set(
								false);
							this.scheduleHeightRecalculation();
						},
						error: () =>
						{
							if (currentSequence !== this.searchRequestSequence)
							{
								return;
							}

							this.results.set([]);
							this.highlightTerms.set(parsedHighlightTerms);
							this.initialSearchError.set(true);
							this.hasSearched.set(true);
							this.loading.set(false);
						},
						complete: () =>
						{
							if (currentSequence === this.searchRequestSequence)
							{
								this.activeSearchSubscription = null;
							}
						}
					});
	}

	/**
	 * Builds the single-entry request body for a load-more call,
	 * applying the current sort, item count, and last-seen Id.
	 *
	 * @private
	 * @param {IInsuranceSearchResultGroup} group
	 * The group being paginated.
	 * @param {IInsuranceSearchRequest} requestConfig
	 * The static request configuration for the group.
	 * @param {string} orderBy
	 * The `orderBy` clause to apply.
	 * @param {number} lastId
	 * The live keyset cursor Id read directly from paging state.
	 * @returns {IInsuranceSearchRequest[]}
	 * A single-element array ready to send as the search request body.
	 * @memberof InsuranceSearchComponent
	 */
	private buildLoadMoreRequestBody(
		group: IInsuranceSearchResultGroup,
		requestConfig: IInsuranceSearchRequest,
		orderBy: string,
		lastId: number): IInsuranceSearchRequest[]
	{
		return [
			{
				...requestConfig,
				orderBy,
				current: group.items.length,
				lastId
			}
		];
	}

	/**
	 * Fires the load-more API call, appends the new results, updates
	 * paging state, and restores the scroll position. Discards the
	 * response when superseded by a newer search sequence.
	 *
	 * @private
	 * @param {IInsuranceSearchResultGroup} group
	 * The group being paginated, used for paging-state updates.
	 * @param {IInsuranceSearchRequest[]} body
	 * The request body to send.
	 * @param {IInsuranceSearchRequest} requestConfig
	 * The static request configuration, used for the page-size limit.
	 * @param {HTMLElement} scrollableContainer
	 * The scroll host element whose position will be restored.
	 * @param {number} previousScrollTop
	 * The scroll offset to restore after results are appended.
	 * @returns {Promise<void>}
	 * @memberof InsuranceSearchComponent
	 */
	private async executeLoadMore(
		group: IInsuranceSearchResultGroup,
		body: IInsuranceSearchRequest[],
		requestConfig: IInsuranceSearchRequest,
		scrollableContainer: HTMLElement,
		previousScrollTop: number): Promise<void>
	{
		const systemId: number =
			Number.parseInt(
				AppConstants.systemId,
				AppConstants.parseRadix);
		const currentSequence: number =
			this.searchRequestSequence;
		const trimmedSearchTerm: string =
			this.searchTerm().trim();

		this.entityInstanceApiService.entityInstanceTypeGroup =
			AppConstants.typeGroups.systems;

		try
		{
			const httpResponse: any =
				await this.entityInstanceApiService
					.executeActionWithPayload<any>(
						systemId,
						InsuranceSearchComponent.searchConfig
							.actionName,
						{ searchRequests: body },
						`?searchTerm=${
							encodeURIComponent(
								trimmedSearchTerm)}`);

			if (currentSequence !== this.searchRequestSequence)
			{
				return;
			}

			const actionResponse: any =
				httpResponse?.body ?? httpResponse;

			const newResults: IInsuranceSearchResultDto[] =
				actionResponse?.value ?? [];

			const existingIds: Set<number> =
				new Set(
					this.results().map(
						(result: IInsuranceSearchResultDto) =>
							result.id));

			const allResults: IInsuranceSearchResultDto[] =
				[
					...this.results(),
					...newResults.filter(
						(result: IInsuranceSearchResultDto) =>
							!existingIds.has(result.id))
				];
			this.results.set(allResults);

			this.applyAutoExpand(
				new Set(
					newResults.map(
						(result: IInsuranceSearchResultDto) =>
							result.id)));

			this.scheduleHeightRecalculation();

			this.updateGroupPagingAfterLoadMore(
				group.groupKey,
				newResults,
				requestConfig.limit);

			queueMicrotask(
				() =>
				{
					scrollableContainer.scrollTop = previousScrollTop;
				});
		}
		catch
		{
			if (currentSequence === this.searchRequestSequence)
			{
				this.setGroupLoadError(
					group.groupKey,
					true);
				this.setGroupLoading(
					group.groupKey,
					false);
			}
		}
	}

	/**
	 * Applies auto-expansion to result cards that have search highlights
	 * in their detail fields. When called without arguments (initial
	 * search), replaces the expanded set entirely. When called with a
	 * `limitToIds` set (load-more), merges only the newly loaded results
	 * into the existing expanded set, preserving manual user expansions.
	 *
	 * @private
	 * @param {Set<number>} [limitToIds]
	 * When provided, only results in this set are evaluated and merged.
	 * Omit to evaluate all results and reset the expanded set.
	 * @memberof InsuranceSearchComponent
	 */
	private applyAutoExpand(limitToIds?: Set<number>): void
	{
		const autoExpanded: Set<number> =
			this.computeAutoExpandedIds(limitToIds);
		const baseIds: Set<number> =
			AnyHelper.isNull(limitToIds)
				? new Set<number>()
				: this.expandedResultIds();

		this.expandedResultIds.set(
			new Set<number>([...baseIds, ...autoExpanded]));
	}

	/**
	 * Computes the set of result IDs that should be auto-expanded
	 * because at least one detail field value contains a search
	 * highlight. When `limitToIds` is provided only those IDs
	 * are considered (used during load-more to avoid re-evaluating
	 * already-visible results).
	 *
	 * @private
	 * @param {Set<number>} [limitToIds]
	 * Optional subset of result IDs to evaluate. When omitted,
	 * all current display items are evaluated.
	 * @returns {Set<number>}
	 * @memberof InsuranceSearchComponent
	 */
	private computeAutoExpandedIds(
		limitToIds?: Set<number>): Set<number>
	{
		const autoExpanded: Set<number> =
			new Set<number>();

		for (const group of this.groupedDisplayResults())
		{
			for (const item of group.displayItems)
			{
				if (!item.isExpandable)
				{
					continue;
				}

				if (limitToIds && !limitToIds.has(item.id))
				{
					continue;
				}

				const hasDetailHighlight: boolean =
					item.detailFields.some(
						(field: IInsuranceSearchFieldDisplay) =>
							field.highlightedValues.some(
								(value: string) =>
									value.includes('search-highlight')));

				if (hasDetailHighlight)
				{
					autoExpanded.add(item.id);
				}
			}
		}

		return autoExpanded;
	}

	/**
	 * Resolves all entity type groups that start with the
	 * PolicyTermTransaction prefix, then loads the user's hierarchy
	 * permissions for those groups along with the other entity type
	 * groups. Sets allowedSearchGroups and enabledSearchGroups based
	 * on the resolved read rights.
	 *
	 * @private
	 * @memberof InsuranceSearchComponent
	 */
	private loadUserPermissions(): void
	{
		const policyGroupFilter: string =
			AppConstants.commonProperties.group
				+ '.StartsWith(\''
				+ InsuranceSearchComponent.policyTermTransactionPrefix
				+ '.\') eq true';

		from(
			this.entityTypeApiService.query(
				policyGroupFilter,
				AppConstants.empty))
			.pipe(
				switchMap(
					(policyTypes: IEntityTypeDto[]) =>
					{
						const policyGroups: string[] =
							policyTypes.map(
								(entityType: IEntityTypeDto) =>
									entityType.group);

						this.entityInstanceApiService.entityInstanceTypeGroup =
							AppConstants.typeGroups.users;

						return from(
							this.entityInstanceApiService
								.getHierarchyPermissions(
									this.sessionService.user.id,
									[
										InsuranceConstants
											.insuranceEntityTypeGroups
											.agencies,
										InsuranceConstants
											.insuranceEntityTypeGroups
											.accounts,
										InsuranceConstants
											.insuranceEntityTypeGroups
											.claims,
										...policyGroups
									]));
					}),
				takeUntilDestroyed(this.destroyRef))
			.subscribe(
				(permissions: ISecurityEntityTypeDefinition[]) =>
				{
					const allowed: Set<string> =
						this.buildAllowedGroups(permissions);

					this.allowedSearchGroups.set(allowed);
					this.enabledSearchGroups.set(
						new Set<string>(allowed));

					requestAnimationFrame(
						() =>
						{
							this.searchInput()?.nativeElement.focus();
						});
				});
	}

	/**
	 * Builds the set of filter labels the current user is permitted
	 * to access based on read-level entity type permissions.
	 *
	 * @private
	 * @param {ISecurityEntityTypeDefinition[]} permissions
	 * The hierarchy permissions resolved for the current user.
	 * @returns {Set<string>}
	 * The set of permitted filter labels.
	 * @memberof InsuranceSearchComponent
	 */
	private buildAllowedGroups(
		permissions: ISecurityEntityTypeDefinition[]): Set<string>
	{
		const allowed: Set<string> = new Set<string>();

		if (SecurityHelper.HasRight(
			SecurityRightType.read,
			InsuranceSearchComponent.entityTypeAccount,
			permissions))
		{
			allowed.add(InsuranceSearchComponent.filterLabelAccount);
		}

		if (SecurityHelper.HasRight(
			SecurityRightType.read,
			InsuranceSearchComponent.entityTypeClaim,
			permissions))
		{
			allowed.add(InsuranceSearchComponent.filterLabelClaim);
		}

		if (SecurityHelper.HasRight(
			SecurityRightType.read,
			InsuranceSearchComponent.entityTypeAgency,
			permissions))
		{
			allowed.add(InsuranceSearchComponent.filterLabelAgency);
		}

		if (permissions.some(
			(permission: ISecurityEntityTypeDefinition) =>
				permission.entityTypeName.startsWith(
					InsuranceSearchComponent.policyTermTransactionPrefix)
				&& permission.securityDefinition?.rights?.read === true))
		{
			allowed.add(InsuranceSearchComponent.filterLabelPolicy);
		}

		return allowed;
	}
}